import { getRoofingMaterialExtension, getRoofingMaterialThickness } from "building-common/roofs/roofQuery";
import { mergeCoplanarFaces } from "building-generation/faceMerge";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import { RoofFaceType } from "building-types/roofs/roofFace";
import type { EvaluatedRoofSettings, RoofSettings } from "building-types/settings/roofSettings";
import type { NeighborRoofs, RidgeRail } from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { Table } from "common/data/table";
import {
  ARBITRARY_ROOFING_CAP_EXTENSION,
  DEFAULT_ROOFING_CAP_THICKNESS,
  DEFAULT_ROOFING_CAP_WIDTH,
  RIDGE_CLIPPING_ORIGIN_RIDGE_END_TOLERANCE,
} from "doc/settings/defaultSettings";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { type Face, tagFaces } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clipFaces, clipFacesWithPlanes, clipRail3, clipRails3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { line2Collinear } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planeBSPFromPlane } from "geom/planeBSP";
import { polyLine2Reverse, polyLine2Close, polyLine2Concat } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Copy,
  polyLine3EdgeTangent,
  polyLine3EndPoint,
  polyLine3EvalEdge,
  polyLine3ExtendBothEnds,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3Move,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { ray3DotProject } from "geom/ray3";
import { vec2Normalize } from "geom/vec2";
import { vec3DistSquared } from "geom/vec3";

const MIN_RIDGE_LENGTH = 0.5;

export function buildRidgeVentRails(
  roofs: Table<GeneratedRoof>,
  ridgeRails: RidgeRail[],
  roofSettings: EvaluatedRoofSettings,
  wallSettings: WallSettings,
  neighborRoofs: NeighborRoofs,
) {
  const { startNeighbor, endNeighbor } = neighborRoofs;
  const { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } = roofSettings;
  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(wallSettings, overhangDatum);
  const {
    ridgeVentEndsOffset = roofSettings.rakeOverhang + exteriorWallOffsetWithSheathing,
    ridgeVentMinimumThreshold = 5,
  } = roofSettings;

  const roofingMaterialExtension = getRoofingMaterialExtension(roofSettings);

  for (const ridgeRail of ridgeRails) {
    const { clippedRails, isHorizontal } = ridgeRail;

    if (!isHorizontal) {
      continue;
    }

    const startNeighborRoof = roofs[startNeighbor.roofId];
    const endNeighborRoof = roofs[endNeighbor.roofId];

    const rails: PolyLine3[] = [];

    for (const clippedRail of clippedRails) {
      const minLength = 2 * (ridgeVentEndsOffset + roofingMaterialExtension) + ridgeVentMinimumThreshold;
      if (polyLine3Length(clippedRail) < minLength) {
        continue;
      }

      const [ax, ay, az] = polyLine3StartPoint(clippedRail);
      const [bx, by] = polyLine3EndPoint(clippedRail);
      const [cx, cy, cz] = startNeighborRoof
        ? polyLine3StartPoint(startNeighborRoof.rails.peakRail)
        : polyLine3StartPoint(clippedRail);
      const [dx, dy] = startNeighborRoof
        ? polyLine3EndPoint(startNeighborRoof.rails.peakRail)
        : polyLine3EndPoint(clippedRail);
      const [ex, ey, ez] = endNeighborRoof
        ? polyLine3StartPoint(endNeighborRoof.rails.peakRail)
        : polyLine3StartPoint(clippedRail);
      const [fx, fy] = endNeighborRoof
        ? polyLine3EndPoint(endNeighborRoof.rails.peakRail)
        : polyLine3EndPoint(clippedRail);

      const rail = polyLine3Copy(clippedRail);
      const startExtension =
        startNeighborRoof && line2Collinear(ax, ay, bx, by, cx, cy, dx, dy) && Math.abs(az - cz) < DIST_EPSILON
          ? ridgeVentEndsOffset -
            2 * (roofSettings.rakeOverhang + roofingMaterialExtension + exteriorWallOffsetWithSheathing)
          : -ridgeVentEndsOffset - roofingMaterialExtension;
      const endExtension =
        endNeighborRoof && line2Collinear(ax, ay, bx, by, ex, ey, fx, fy) && Math.abs(az - ez) < DIST_EPSILON
          ? ridgeVentEndsOffset -
            2 * (roofSettings.rakeOverhang + roofingMaterialExtension + exteriorWallOffsetWithSheathing)
          : -ridgeVentEndsOffset - roofingMaterialExtension;

      polyLine3ExtendBothEnds(rail, startExtension, endExtension);

      rails.push(rail);
    }

    ridgeRail.ridgeVentRails = rails;
  }
}

export function buildRidgeVents(ridgeRails: RidgeRail[]): Face[] {
  const ridgeVentFaces: Face[] = [];

  for (const { ridgeVentRails, ventProfile, addRidgeVent } of ridgeRails) {
    if (!addRidgeVent) {
      continue;
    }

    for (const ridgeVentRail of ridgeVentRails) {
      const brep = extrudeFaceAlongPolyLine3([ventProfile], ridgeVentRail, 0, 0, 1);

      ridgeVentFaces.push(...brep.faces);
    }
  }

  return ridgeVentFaces;
}

const TAG_ROOF_RIDGE_CAP = { type: "CONDITION", condition: "ROOF_RIDGE_CAP" };

export function buildRidgeCaps(ridgeRails: RidgeRail[], clipContext: MultiGableRoofClipContext): Face[] {
  const { insideBSP } = clipContext;

  const toleranceSquared = RIDGE_CLIPPING_ORIGIN_RIDGE_END_TOLERANCE * RIDGE_CLIPPING_ORIGIN_RIDGE_END_TOLERANCE;
  const ridgeCapFaces: Face[] = [];
  for (const { rail, clippedRails, capProfile, clippingPlanes, isHorizontal } of ridgeRails) {
    const [srcStartX, srcStartY, srcStartZ] = polyLine3StartPoint(rail);
    for (const clippedRail of clippedRails) {
      let extendedRidgeRail = clippedRail;

      if (!isHorizontal) {
        const [currStartX, currStartY, currStartZ] = polyLine3StartPoint(clippedRail);

        // extend the rail only if the start point is the same as the source start point
        if (vec3DistSquared(srcStartX, srcStartY, srcStartZ, currStartX, currStartY, currStartZ) < toleranceSquared) {
          extendedRidgeRail = polyLine3ExtendStart(polyLine3Copy(clippedRail), ARBITRARY_ROOFING_CAP_EXTENSION);
        }
      } else {
        extendedRidgeRail = clippedRail;
      }

      const brep = extrudeFaceAlongPolyLine3([capProfile], extendedRidgeRail, 0, 0, 1);
      let faces = clipFacesWithPlanes({
        faces: brep.faces,
        planes: clippingPlanes,
        epsilon: MULTIGABLE_BSP_EPSILON,
        addCapFaces: true,
      });

      faces = clipFaces({
        faces,
        options: {
          clippingNodes: [clippingNodeNew(insideBSP)],
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
      });

      faces = mergeCoplanarFaces({
        inputFaces: faces,
        simplifyDistThresholdSq: DIST_EPSILON_SQUARED,
        simplifyAngleThreshold: ANGLE_EPSILON,
      });

      ridgeCapFaces.push(...tagFaces(faces, TAG_ROOF_RIDGE_CAP));
    }
  }

  return ridgeCapFaces;
}

interface ClipRidgesOptions {
  subRoof: GeneratedRoof;
  ridgeRails: RidgeRail[];
  clipContext: MultiGableRoofClipContext;
  roofTopClippingBSPs: (NodeBSP | null)[];
  limitBSP?: NodeBSP | null;
}

export function clipRidges({
  subRoof,
  ridgeRails,
  clipContext,
  roofTopClippingBSPs,
  limitBSP = null,
}: ClipRidgesOptions) {
  const { roofTopFaces } = subRoof;
  const { ridgeDiscardBSP } = clipContext;
  const clipRidgeRailsOptions: ClipRailContext = {
    clippingNodes: limitBSP
      ? [clippingNodeNew(limitBSP), clippingNodeNew(ridgeDiscardBSP)]
      : [clippingNodeNew(ridgeDiscardBSP)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };
  const extraHipFaces = roofTopFaces.filter((x) => x.type === RoofFaceType.HIP && x.side === RoofSide.INVALID);
  const extraHipPlanes = extraHipFaces.map((x) => planeBSPFromPlane(x.face.plane));
  const extraHipBSP = addConvexPlanesToBSPLeaf(extraHipPlanes);

  const extraClipOpions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(extraHipBSP, true), ...roofTopClippingBSPs.map((x) => clippingNodeNew(x))],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // iterate all ridge rails
  const ridgeRailOffsetZ = -0.5;
  for (const ridgeRail of ridgeRails) {
    const { rail, leftSlope, rightSlope } = ridgeRail;
    let clippedRidgeRails: PolyLine3[] = [];

    // build 2 offset rails for each ridge rail
    const [tX, tY] = polyLine3EdgeTangent(rail, 0);
    const [nX, nY] = vec2Normalize(-tY, tX);
    const leftOffsetX = ridgeRailOffsetZ / leftSlope;
    const rightOffsetX = ridgeRailOffsetZ / rightSlope;

    const [originX, originY, originZ] = polyLine3EvalEdge(rail, 0, 0);

    const rail1 = polyLine3Move(polyLine3Copy(rail), nX * rightOffsetX, nY * rightOffsetX, ridgeRailOffsetZ);
    const rail2 = polyLine3Move(polyLine3Copy(rail), -nX * leftOffsetX, -nY * leftOffsetX, ridgeRailOffsetZ);

    // clip the offset rails
    const subRails1 = clipRail3({ rail: rail1, options: clipRidgeRailsOptions });
    const subRails2 = clipRail3({ rail: rail2, options: clipRidgeRailsOptions });

    // iterate clipped rails for one of the offset rails
    for (let i = 0, li = subRails1.length; i < li; i++) {
      const subRail1 = subRails1[i];
      const subRailLen1 = polyLine3Length(subRail1);
      const [dirX, dirY, dirZ] = polyLine3EdgeTangent(subRail1, 0);
      const [oX, oY, oZ] = polyLine3EvalEdge(subRail1, 0, 0);

      // check against the clipped rails from the other offset rail
      for (let j = 0, lj = subRails2.length; j < lj; j++) {
        const subRail2 = subRails2[j];
        const [x0, y0, z0] = polyLine3EvalEdge(subRail2, 0, 0);
        const [x1, y1, z1] = polyLine3EvalEdge(subRail2, 0, 1, true);

        // project 2 over 1
        const dot0 = Math.min(Math.max(ray3DotProject(oX, oY, oZ, dirX, dirY, dirZ, x0, y0, z0), 0), subRailLen1);
        const dot1 = Math.min(Math.max(ray3DotProject(oX, oY, oZ, dirX, dirY, dirZ, x1, y1, z1), 0), subRailLen1);
        const diff = dot1 - dot0;

        // check for min overlap
        if (diff > MIN_RIDGE_LENGTH) {
          const dot = Math.max(
            ray3DotProject(originX, originY, originZ, dirX, dirY, dirZ, x0, y0, z0),
            ray3DotProject(originX, originY, originZ, dirX, dirY, dirZ, oX, oY, oZ),
          );

          clippedRidgeRails.push(
            polyLine3ByTwoPoints(
              originX + dirX * dot,
              originY + dirY * dot,
              originZ + dirZ * dot,
              originX + dirX * (dot + diff),
              originY + dirY * (dot + diff),
              originZ + dirZ * (dot + diff),
            ),
          );
        }
      }
    }

    clippedRidgeRails = clipRails3({
      rails: clippedRidgeRails,
      options: extraClipOpions,
    });

    ridgeRail.clippedRails = clippedRidgeRails;
  }
}

export function buildRidgeVentProfile(leftSideSlope: number, rightSideSlope: number): PolyLine2 {
  const ridgeVentThickness = 0.1;
  const ridgeVentHalfWidth = 5.5;
  const ridgeVentOffsetY = 0.5;

  const leftAngle = Math.atan(leftSideSlope);
  const rightAngle = Math.atan(rightSideSlope);

  const leftSin = Math.sin(leftAngle);
  const leftCos = Math.cos(leftAngle);

  const rightSin = Math.sin(rightAngle);
  const rightCos = Math.cos(rightAngle);

  return polyLine2New([
    0,
    ridgeVentOffsetY,
    rightCos * ridgeVentHalfWidth,
    ridgeVentOffsetY - rightSin * ridgeVentHalfWidth,
    rightCos * ridgeVentHalfWidth,
    ridgeVentOffsetY - rightSin * ridgeVentHalfWidth - ridgeVentThickness,
    0,
    ridgeVentOffsetY - ridgeVentThickness,
    -leftCos * ridgeVentHalfWidth,
    ridgeVentOffsetY - leftSin * ridgeVentHalfWidth - ridgeVentThickness,
    -leftCos * ridgeVentHalfWidth,
    ridgeVentOffsetY - leftSin * ridgeVentHalfWidth,
    0,
    ridgeVentOffsetY,
  ]);
}

export function buildRidgeCapProfile(
  leftSideSlope: number,
  rightSideSlope: number,
  roofSettings: RoofSettings,
): PolyLine2 {
  const {
    roofRidgeRoofingCapWidth = DEFAULT_ROOFING_CAP_WIDTH,
    roofRidgeRoofingCapThickness = DEFAULT_ROOFING_CAP_THICKNESS,
  } = roofSettings;

  const roofingMaterialThickness = getRoofingMaterialThickness(roofSettings);

  const leftAngle = Math.atan(leftSideSlope);
  const rightAngle = Math.atan(rightSideSlope);

  const leftSin = Math.sin(leftAngle);
  const leftCos = Math.cos(leftAngle);

  const rightSin = Math.sin(rightAngle);
  const rightCos = Math.cos(rightAngle);

  let pl1 = polyLine2New([
    rightCos * roofRidgeRoofingCapWidth,
    -rightSin * roofRidgeRoofingCapWidth,
    0,
    0,
    -leftCos * roofRidgeRoofingCapWidth,
    -leftSin * roofRidgeRoofingCapWidth,
  ]);

  pl1 = offsetPolyLine2(pl1, roofingMaterialThickness);

  let pl2 = offsetPolyLine2(pl1, roofRidgeRoofingCapThickness);
  pl2 = polyLine2Reverse(pl2);

  return polyLine2Close(polyLine2Concat(pl1, pl2));
}

//Useful for visual debugging
// function debugDisplayTrimLines(pl: Float64Array, id: string, lineColor: LineBasicMaterial, signal: string) {
//   if (pl) {
//     debugDisplay({
//       id: id,
//       objects: [polyLine2ToTHREEDebug(pl, debugPlane, lineColor)],
//       signals: [signal],
//     });
//   }
// }

// function logNodeBSP(node: NodeBSP) {
//   if (node) {
//     console.log("node plane - ", counter++, node.plane);
//   }
//   if (node.frontNode !== null) {
//     logNodeBSP(node.frontNode);
//   }
//   if (node.backNode !== null) {
//     logNodeBSP(node.backNode);
//   }
// }

// function solveNodeBSP(node: NodeBSP, pl: PolyLine3) {
//   if (node) {
//     const { plane } = node;
//     const { nX, nY, nZ, dist } = plane;
//     const [x1, y1, z1] = polyLine3StartPoint(pl);
//     const [x2, y2, z2] = polyLine3EndPoint(pl);
//     const d1 = nX * x1 + nY * y1 + nZ * z1 - dist;
//     const d2 = nX * x2 + nY * y2 + nZ * z2 - dist;
//     console.log("distance from node plane - ", counter++, d1, d2);
//   }
//   if (node.frontNode !== null) {
//     solveNodeBSP(node.frontNode, pl);
//   }
//   if (node.backNode !== null) {
//     solveNodeBSP(node.backNode, pl);
//   }
// }
